import React, { useState, useEffect } from "react";
import {
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  IconButton,
  Select,
  Switch,
  FormControlLabel,
  Tooltip,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { makeStyles } from 'tss-react/mui';
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import { i18n } from "../../translate/i18n";

import { Edit as EditIcon, BusinessOutlined } from "@mui/icons-material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from "../../services/toast";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import ModalUsers from "../ModalUsers";
import api from "../../services/api";
import { head, isArray, has } from "lodash";
import { useDate } from "../../hooks/useDate";
import toastError from "../../errors/toastError";
import { useNavigate } from "react-router-dom";
import { socketConnection } from "../../services/socket";
import SettingsCard from "../Settings/SettingsCard";
import FormSectionLabel from "../FormSectionLabel";
import { StandardTableContainer, useStandardTableStyles } from "../StandardTable";

import moment from "moment";

const useStyles = makeStyles()((theme) => ({
  root: {
    width: "100%",
  },
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(1),
    background: "transparent",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  fullWidth: {
    width: "100%",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    marginTop: theme.spacing(1),
  },
  statusChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
}));

const DUE_DATE_URGENCY = (dueDate) => {
  if (!moment(dueDate).isValid()) return null;
  const diff = moment(dueDate).diff(moment(), "days");
  if (diff < -3) return "error";
  if (diff <= 5) return "warning";
  return null;
};

export function CompanyForm(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const { classes } = useStyles();
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    ...initialValue,
  });

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const list = await listPlans();
      setPlans(list);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRecord((prev) => {
      if (moment(initialValue).isValid()) {
        initialValue.dueDate = moment(initialValue.dueDate).format(
          "YYYY-MM-DD"
        );
      }
      return {
        ...prev,
        ...initialValue,
      };
    });
  }, [initialValue]);

  const handleSubmit = async (data) => {
    if (data.dueDate === "" || moment(data.dueDate).isValid() === false) {
      data.dueDate = null;
    }
    onSubmit(data);
    setRecord({ ...initialValue, dueDate: "" });
  };

  const handleOpenModalUsers = async () => {
    try {
      const { data } = await api.get("/users/list", {
        params: {
          companyId: initialValue.id,
        },
      });
      if (isArray(data) && data.length) {
        setFirstUser(head(data));
      }
      setModalUser(true);
    } catch (e) {
      toast.error(e);
    }
  };

  const handleCloseModalUsers = () => {
    setFirstUser({});
    setModalUser(false);
  };

  const incrementDueDate = () => {
    const data = { ...record };
    if (data.dueDate !== "" && data.dueDate !== null) {
      switch (data.recurrence) {
        case "MENSAL":
          data.dueDate = moment(data.dueDate)
            .add(1, "month")
            .format("YYYY-MM-DD");
          break;
        case "BIMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(2, "month")
            .format("YYYY-MM-DD");
          break;
        case "TRIMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(3, "month")
            .format("YYYY-MM-DD");
          break;
        case "SEMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(6, "month")
            .format("YYYY-MM-DD");
          break;
        case "ANUAL":
          data.dueDate = moment(data.dueDate)
            .add(12, "month")
            .format("YYYY-MM-DD");
          break;
        default:
          break;
      }
    }
    setRecord(data);
  };

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={initialValue.id}
        open={modalUser}
        onClose={handleCloseModalUsers}
      />
      <Formik
        enableReinitialize
        className={classes.fullWidth}
        initialValues={record}
        onSubmit={(values, { resetForm }) =>
          setTimeout(() => {
            handleSubmit(values);
            resetForm();
          }, 500)
        }
      >
        {({ values, setFieldValue }) => (
          <Form className={classes.fullWidth}>
            <Grid spacing={1.5} container>
              <FormSectionLabel>Datos generales</FormSectionLabel>
              <Grid size={12}>
                <Field
                  as={TextField}
                  label="Nombre"
                  name="name"
                  variant="outlined"
                  size="small"
                  className={classes.fullWidth}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  label="Email"
                  name="email"
                  variant="outlined"
                  size="small"
                  className={classes.fullWidth}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  label="Telefono"
                  name="phone"
                  variant="outlined"
                  size="small"
                  className={classes.fullWidth}
                />
              </Grid>

              <FormSectionLabel>Plan y acceso</FormSectionLabel>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl size="small" variant="outlined" fullWidth>
                  <InputLabel htmlFor="plan-selection">Plan</InputLabel>
                  <Field
                    as={Select}
                    id="plan-selection"
                    label="Plan"
                    labelId="plan-selection-label"
                    name="planId"
                    required
                  >
                    {plans.map((plan, key) => (
                      <MenuItem key={key} value={plan.id}>
                        {plan.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(values.status)}
                      onChange={(e) => setFieldValue("status", e.target.checked)}
                    />
                  }
                  label="Activa"
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(values.campaignsEnabled)}
                      onChange={(e) => setFieldValue("campaignsEnabled", e.target.checked)}
                    />
                  }
                  label="Campañas"
                />
              </Grid>

              <FormSectionLabel>Facturación</FormSectionLabel>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  label="Vencimiento"
                  type="date"
                  name="dueDate"
                  slotProps={{ inputLabel: { shrink: true } }}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl size="small" variant="outlined" fullWidth>
                  <InputLabel htmlFor="recorrencia-selection">
                    Repeticion
                  </InputLabel>
                  <Field
                    as={Select}
                    label="Repeticion"
                    labelId="recorrencia-selection-label"
                    id="recurrence"
                    name="recurrence"
                  >
                    <MenuItem value="MENSAL">Mensal</MenuItem>
                    <MenuItem value="BIMESTRAL">Bimestral</MenuItem>
                    <MenuItem value="TRIMESTRAL">Trimestral</MenuItem>
                    <MenuItem value="SEMESTRAL">Semestral</MenuItem>
                    <MenuItem value="ANUAL">Anual</MenuItem>
                  </Field>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <div className={classes.actions}>
                  <ButtonWithSpinner
                    loading={loading}
                    onClick={() => onCancel()}
                    variant="outlined"
                  >
                    Cancelar
                  </ButtonWithSpinner>
                  {record.id !== undefined && (
                    <>
                      <ButtonWithSpinner
                        loading={loading}
                        onClick={() => onDelete(record)}
                        variant="outlined"
                        color="secondary"
                      >
                        Eliminar
                      </ButtonWithSpinner>
                      <ButtonWithSpinner
                        loading={loading}
                        onClick={() => incrementDueDate()}
                        variant="outlined"
                        color="primary"
                      >
                        + Vencimiento
                      </ButtonWithSpinner>
                      <ButtonWithSpinner
                        loading={loading}
                        onClick={() => handleOpenModalUsers()}
                        variant="outlined"
                        color="primary"
                      >
                        Usuario
                      </ButtonWithSpinner>
                    </>
                  )}
                  <ButtonWithSpinner
                    loading={loading}
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    Guardar
                  </ButtonWithSpinner>
                </div>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
}

export function CompaniesManagerGrid(props) {
  const { records, onSelect } = props;
  const { classes } = useStyles();
  const tableClasses = useStandardTableStyles();
  const { dateToClient } = useDate();

  const [user, setUser] = useState({});
  const navigate = useNavigate();

  const campaignsStatus = (row) => {
    if (has(row, "settings") && isArray(row.settings) && row.settings.length > 0) {
      const setting = row.settings.find((s) => s.key === "campaignsEnabled");
      if (setting) {
        return setting.value === "true" ? "Campañas habilitadas" : null;
      }
    }
    return null;
  };

  const autoLogin = async (dataUser) => {
    let userId = dataUser.email;
    if (!userId) return;
    try {
      const { data } = await api.get(`/usersSwitch/${userId}`);

      const {
        user: { companyId, id, company },
      } = data;

      localStorage.removeItem("companyId");
      localStorage.removeItem("userId");

      if (has(company, "settings") && isArray(company.settings)) {
        const setting = company.settings.find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }
      const socket = socketConnection({ companyId });

      socket.on(`company-${companyId}-user`, (data) => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser(data.user);
        }
      });

      const dueDate = data.user.company.dueDate;
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      localStorage.setItem("token", JSON.stringify(data.token));
      localStorage.setItem("companyId", companyId);
      localStorage.setItem("userId", id);
      localStorage.setItem("companyDueDate", vencimento);
      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      setUser(data.user);

      toast.success(i18n.t("auth.toasts.success"));

      window.location.href = '/tickets';
    } catch (err) {
      toastError(err);
    }
  }

  return (
    <StandardTableContainer>
      <Table size="small" stickyHeader>
        <TableHead className={tableClasses.head}>
          <TableRow>
            <TableCell>Nombre</TableCell>
            <TableCell>Contacto</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell align="center">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row) => {
            const urgency = DUE_DATE_URGENCY(row.dueDate);
            const campaigns = campaignsStatus(row);
            return (
              <TableRow key={row.id} hover>
                <TableCell>{row.name || '-'}</TableCell>
                <TableCell>
                  {row.email || '-'} {row.phone ? `· ${row.phone}` : ''}
                </TableCell>
                <TableCell>
                  <div className={classes.statusChips}>
                    <Chip size="small" variant="outlined" label={row.planId !== null ? row.plan.name : "Sin plan"} />
                    {row.status === false && <Chip size="small" color="default" variant="outlined" label="Inactiva" />}
                    {campaigns && <Chip size="small" variant="outlined" label={campaigns} />}
                    {row.dueDate && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={urgency || "default"}
                        label={`Vence ${dateToClient(row.dueDate)}${row.recurrence ? ` · ${row.recurrence}` : ''}`}
                      />
                    )}
                  </div>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Acceder a la empresa">
                    <IconButton aria-label="acceder" onClick={() => autoLogin(row)} size="small">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton aria-label="editar" onClick={() => onSelect(row)} size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {!records.length && (
        <div className={tableClasses.empty}>Todavía no hay ninguna empresa cargada.</div>
      )}
    </StandardTableContainer>
  );
}

export default function CompaniesManager() {
  const { classes } = useStyles();
  const { list, save, update, remove } = useCompanies();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
  });

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const companyList = await list();
      setRecords(companyList);
    } catch (e) {
      toast.error("No se puede cargar la lista de registros");
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadPlans();
      handleCancel();
      setModalOpen(false);
      toast.success("¡Operación realizada con éxito!");
    } catch (e) {
      toast.error(
        "No se puede realizar la operación. Compruebe si ya existe una empresa con el mismo nombre o si los campos se han completado correctamente"
      );
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCancel();
      setModalOpen(false);
      toast.success("¡Operación realizada con éxito!");
    } catch (e) {
      toast.error("No se puede realizar la operación");
    }
    setLoading(false);
  };

  const openNewModal = () => {
    handleCancel();
    setRecord((prev) => ({ ...prev, id: undefined }));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    handleCancel();
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setRecord((prev) => ({
      ...prev,
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: true,
      campaignsEnabled: false,
      dueDate: "",
      recurrence: "",
    }));
  };


  const handleSelect = (data) => {
    let campaignsEnabled = false;

    const setting = data.settings.find(
      (s) => s.key.indexOf("campaignsEnabled") > -1
    );
    if (setting) {
      campaignsEnabled =
        setting.value === "true" || setting.value === "enabled";
    }

    setRecord((prev) => ({
      ...prev,
      id: data.id,
      name: data.name || "",
      phone: data.phone || "",
      email: data.email || "",
      planId: data.planId || "",
      status: data.status === false ? false : true,
      campaignsEnabled,
      dueDate: data.dueDate || "",
      recurrence: data.recurrence || "",
    }));
    setModalOpen(true);
  };

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <SettingsCard
        icon={BusinessOutlined}
        title={i18n.t("settings.tabs.companies")}
        subtitle="Las empresas (tenants) que usan la plataforma, su plan y sus datos de contacto."
        action={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openNewModal}
          >
            Nueva empresa
          </Button>
        }
      />

      <Dialog open={modalOpen} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {record.id ? "Editar empresa" : "Nueva empresa"}
        </DialogTitle>
        <DialogContent dividers>
          <CompanyForm
            initialValue={record}
            onDelete={handleOpenDeleteDialog}
            onSubmit={handleSubmit}
            onCancel={closeModal}
            loading={loading}
          />
        </DialogContent>
      </Dialog>

      <CompaniesManagerGrid records={records} onSelect={handleSelect} />
      <ConfirmationModal
        title="Eliminacion del registro"
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        ¿Realmente desea eliminar este registro?
      </ConfirmationModal>
    </Paper>
  );
}
